import React from "react";
import { FaRegHeart, FaHeart, FaArrowRight, FaMapMarkerAlt } from "react-icons/fa";

/**
 * Card de apresentação usado por Construtoras (Incorporação) e InvestirMorar
 * (Dos nossos clientes). Puramente apresentacional: recebe o imóvel já
 * filtrado e os callbacks de navegação/favorito prontos — não busca dados,
 * não decide regras de negócio.
 *
 * O <a> real, o href e a lógica de clique (esquerdo simples navega via
 * router; Ctrl/Cmd/Shift/clique do meio abrem nativamente em nova guia)
 * são preservados exatamente como no componente original.
 */
function PropertyShowcaseCard({ property, isFavorite, onToggleFavorite, onNavigate }) {
  const { imovelInfo, caracteristicas, informacoesComerciais, informacoesAdicionais } = property;

  const bairro = imovelInfo?.bairro;

  const primaryFeatures = [
    caracteristicas?.quartos > 0 && `${caracteristicas.quartos} Quartos`,
    caracteristicas?.banheiros > 0 && `${caracteristicas.banheiros} Banheiros`,
    caracteristicas?.vagas > 0 && `${caracteristicas.vagas} Vagas`,
  ].filter(Boolean);

  const secondaryFeatures = [
    caracteristicas?.suites > 0 && `${caracteristicas.suites} Suítes`,
    caracteristicas?.areaConstruida > 0 && `${caracteristicas.areaConstruida} m² Área Construída`,
    caracteristicas?.areaTerreno > 0 && `${caracteristicas.areaTerreno} m² Área Terreno`,
  ].filter(Boolean);

  return (
    <a
      href={`/#/imovel/${imovelInfo.codigo}`}
      className="showcase-card"
      draggable="false"
      onDragStart={(e) => e.preventDefault()}
      onClick={(e) => {
        // Evita recarregar a página caso seja clique esquerdo
        if (
          e.button === 0 && // botão esquerdo
          !e.metaKey && // não cmd (Mac)
          !e.ctrlKey && // não ctrl (Windows)
          !e.shiftKey && // não shift
          !e.altKey // não alt
        ) {
          e.preventDefault();
          onNavigate(imovelInfo.codigo);
        }
        // Qualquer outro clique (scroll, ctrl+click, cmd+click) abrirá nativamente
      }}
      target="_self"
      rel="noopener noreferrer"
    >
      <div className="showcase-card__photo">
        {informacoesAdicionais?.exclusivo === "true" && (
          <span className="showcase-card__badge">Imóvel exclusivo</span>
        )}

        <button
          type="button"
          className="showcase-card__favorite"
          aria-label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
          onClick={(e) => {
            e.preventDefault(); // impede o <a> de navegar
            e.stopPropagation(); // impede a propagação pro link
            onToggleFavorite(property);
          }}
        >
          {isFavorite ? <FaHeart color="var(--verde)" /> : <FaRegHeart />}
        </button>

        <img
          src={property.imagesLinks?.[0] || "https://via.placeholder.com/400x500"}
          alt={`Imóvel ${imovelInfo.codigo}`}
          loading="lazy"
          decoding="async"
          width="400"
          height="500"
          draggable="false"
        />
      </div>

      <div className="showcase-card__content">
        <p className="showcase-card__meta">
          Cód. {imovelInfo.codigo} · {imovelInfo.tipo}
        </p>

        {bairro && (
          <p className="showcase-card__location">
            <FaMapMarkerAlt /> <span>{bairro}</span>
          </p>
        )}

        <h4 className="showcase-card__title">{imovelInfo.titulo}</h4>

        {(primaryFeatures.length > 0 || secondaryFeatures.length > 0) && (
          <div className="showcase-card__features">
            {primaryFeatures.length > 0 && (
              <p className="showcase-card__features-primary">
                {primaryFeatures.join(" · ")}
              </p>
            )}
            {secondaryFeatures.length > 0 && (
              <p className="showcase-card__features-secondary">
                {secondaryFeatures.join(" · ")}
              </p>
            )}
          </div>
        )}

        <div className="showcase-card__footer">
          <div className="showcase-card__price">
            <span className="showcase-card__price-label">
              {informacoesComerciais.tipoVenda}
            </span>
            <span className="showcase-card__price-value">
              {informacoesComerciais?.valor || "N/A"}
            </span>
          </div>
          <span className="showcase-card__cta">
            Ver imóvel <FaArrowRight aria-hidden="true" />
          </span>
        </div>
      </div>
    </a>
  );
}

export default PropertyShowcaseCard;
